/**
 * What `qrToSvg` accepts as a fill: a CSS colour, or `none`. A value is admitted by
 * this grammar or not written at all, because escaping alone would keep a bad value
 * inside its attribute and still paint it black, and a black ground hides the code.
 * The grammar is written out rather than asked of `CSS.supports` because the encoder
 * runs in Node too, and has to produce the same bytes there.
 *
 * Nothing admitted can load anything or leave an attribute: `url()` and every other
 * function outside this list are refused, as is any character outside `MARKUP_SAFE`.
 * `calc()` and relative colours are refused too: no caller paints a code with them.
 */

const MARKUP_SAFE = /^[\w \t\r\n\f.,%/#()+-]+$/

// Longer than any colour anyone writes, and it bounds the recursion through nested fallbacks.
const LONGEST_PAINT = 256

const HEX = /^#(?:[\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/
const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?(?:%|deg|grad|rad|turn)?$/
const PERCENTAGE = /^(?:\d+\.?\d*|\.\d+)%$/
const CUSTOM_PROPERTY = /^--[\w-]+$/
const SPACE = /[ \t\r\n\f]/

const RECTANGULAR_SPACES = new Set(
	'srgb srgb-linear display-p3 a98-rgb prophoto-rgb rec2020 xyz xyz-d50 xyz-d65'.split(' '),
)
const MIX_WORDS = new Set([
	...RECTANGULAR_SPACES,
	...'lab oklab hsl hwb lch oklch shorter longer increasing decreasing hue'.split(' '),
])

export const COLOR_NAMES: ReadonlySet<string> = new Set(
	`transparent currentcolor
	aliceblue antiquewhite aqua aquamarine azure beige bisque black blanchedalmond blue blueviolet brown burlywood
	cadetblue chartreuse chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan darkgoldenrod darkgray
	darkgreen darkgrey darkkhaki darkmagenta darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen
	darkslateblue darkslategray darkslategrey darkturquoise darkviolet deeppink deepskyblue dimgray dimgrey dodgerblue
	firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green greenyellow grey honeydew
	hotpink indianred indigo ivory khaki lavender lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan
	lightgoldenrodyellow lightgray lightgreen lightgrey lightpink lightsalmon lightseagreen lightskyblue lightslategray
	lightslategrey lightsteelblue lightyellow lime limegreen linen magenta maroon mediumaquamarine mediumblue
	mediumorchid mediumpurple mediumseagreen mediumslateblue mediumspringgreen mediumturquoise mediumvioletred
	midnightblue mintcream mistyrose moccasin navajowhite navy oldlace olive olivedrab orange orangered orchid
	palegoldenrod palegreen paleturquoise palevioletred papayawhip peachpuff peru pink plum powderblue purple
	rebeccapurple red rosybrown royalblue saddlebrown salmon sandybrown seagreen seashell sienna silver skyblue
	slateblue slategray slategrey snow springgreen steelblue tan teal thistle tomato turquoise violet wheat white
	whitesmoke yellow yellowgreen
	accentcolor accentcolortext activetext buttonborder buttonface buttontext canvas canvastext field fieldtext graytext
	highlight highlighttext linktext mark marktext selecteditem selecteditemtext visitedtext`.split(/\s+/),
)

/** Splits on `separator` outside parentheses. A stray parenthesis stays in its piece, which no rule below accepts. */
function split(text: string, separator: RegExp): string[] {
	const pieces = ['']
	let depth = 0
	for (const char of text) {
		if (char === '(') depth++
		if (char === ')') depth--
		if (depth === 0 && separator.test(char)) pieces.push('')
		else pieces[pieces.length - 1] += char
	}
	return pieces.map(piece => piece.trim())
}

const words = (text: string) => split(text, SPACE).filter(Boolean)

function isChannel(channel: string): boolean {
	if (channel === 'none' || NUMBER.test(channel)) return true
	const [property, fallback, ...rest] = /^var\((.*)\)$/s.exec(channel)?.[1].split(',') ?? []
	return (
		CUSTOM_PROPERTY.test(property?.trim() ?? '') &&
		rest.length === 0 &&
		(fallback === undefined || words(fallback).every(word => NUMBER.test(word)))
	)
}

/** `1 2 3` or `1 2 3 / 4`. One `var()` can stand for several channels, so it leaves the count open. */
function hasChannels(body: string): boolean {
	const [main, alpha, ...rest] = split(body, /\//)
	const channels = words(main)
	return (
		rest.length === 0 &&
		(channels.length === 3 || channels.some(channel => channel.startsWith('var('))) &&
		channels.every(isChannel) &&
		(alpha === undefined || isChannel(alpha))
	)
}

/** `1, 2, 3` or `1, 2, 3, 4`, which only the `rgb` and `hsl` families take. */
function hasCommaChannels(body: string): boolean {
	const channels = split(body, /,/)
	return (channels.length === 3 || channels.length === 4) && channels.every(isChannel)
}

const hasNoComma = (body: string) => split(body, /,/).length === 1

function isColor(css: string): boolean {
	if (HEX.test(css) || COLOR_NAMES.has(css)) return true
	const [, name, body] = /^([a-z-]+)\((.*)\)$/s.exec(css) ?? []
	switch (name) {
		case 'rgb':
		case 'rgba':
		case 'hsl':
		case 'hsla':
			return hasNoComma(body) ? hasChannels(body) : hasCommaChannels(body)
		case 'hwb':
		case 'lab':
		case 'lch':
		case 'oklab':
		case 'oklch':
			return hasNoComma(body) && hasChannels(body)
		case 'color': {
			const [space, ...channels] = words(body)
			return hasNoComma(body) && RECTANGULAR_SPACES.has(space) && hasChannels(channels.join(' '))
		}
		case 'color-mix': {
			const [method, ...stops] = split(body, /,/)
			const [keyword, ...mix] = words(method)
			return (
				keyword === 'in' &&
				mix.length > 0 &&
				mix.every(word => MIX_WORDS.has(word)) &&
				stops.length === 2 &&
				stops.every(stop => {
					const [color, ...others] = words(stop).filter(word => !PERCENTAGE.test(word))
					return others.length === 0 && color !== undefined && words(stop).length <= 2 && isColor(color)
				})
			)
		}
		case 'light-dark': {
			const colors = split(body, /,/)
			return colors.length === 2 && colors.every(isColor)
		}
		case 'var': {
			const [property, fallback, ...rest] = split(body, /,/)
			return CUSTOM_PROPERTY.test(property) && rest.length === 0 && (fallback === undefined || isColor(fallback))
		}
		default:
			return false
	}
}

export function isPaint(value: unknown): value is string {
	if (typeof value !== 'string' || value.length > LONGEST_PAINT || !MARKUP_SAFE.test(value)) return false
	const css = value.trim().toLowerCase()
	return css === 'none' || isColor(css)
}
